import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { T, F, Btn } from "@/lib/bricks";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { sourceLabel } from "@/lib/applicantSource";
import { fmtDay } from "@/lib/dates";
import { Page, Section, List, Row, Meta, Empty, Loading, TextLink, StatusText, usePalette, humanize } from "@/components/calm/Calm";
import { ApplicantReviewSection, ReviewScorePill, availabilityLines } from "@/components/hiring/ApplicantReview";
import InsuranceSection, { TierInsuranceFlag } from "@/components/hiring/InsuranceSection";
import VettingSection, { TradeBadge, VettingChip } from "@/components/hiring/VettingSection";

const ONBOARDING_PACK_URL = "https://github.com/bricks-group/bricks-internal/tree/main/hiring/onboarding";

const TIER_LABEL = { 1: "Tier C", 2: "Tier B", 3: "Tier A" };

// Label | value pairs, stacked tight on a phone.
function Facts({ rows, p }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(88px, 120px) minmax(0, 1fr)", rowGap: 6, columnGap: 12, fontSize: 13.5 }}>
      {rows.map(([k, val]) => (
        <div key={k} style={{ display: "contents" }}>
          <span style={{ fontSize: 12.5, color: p.ink3, paddingTop: 1 }}>{k}</span>
          <span style={{ color: p.ink, minWidth: 0, overflowWrap: "anywhere" }}>{val}</span>
        </div>
      ))}
    </div>
  );
}

export default function ApplicantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { p, dark } = usePalette();
  const { user } = useAuth();
  const showOnboardingPack = !!user?.is_platform_owner;

  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [params] = useSearchParams();
  // ?action=approve (from the review email) only highlights the buttons. Approving stays a manual click.
  const wantsDecision = params.get("action") === "approve";

  const load = (quiet) => {
    if (!quiet) setLoading(true);
    return api.get(`/admin/vendors/${id}`)
      .then((r) => { setV(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, [id]);

  const act = async (action, tier) => {
    setBusy(true);
    try {
      await api.patch(`/admin/vendors/${id}`, { action, tier });
      toast.success({ approve: "Approved. Onboarding email sent", reject: "Applicant rejected", unreject: "Moved back to pending", pause: "Paused. No new jobs", resume: "Resumed" }[action] || "Saved");
      load(true);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || (typeof d === "string" ? d : null))) || "Failed to update applicant");
    } finally {
      setBusy(false);
    }
  };

  const rerun = async () => {
    setReviewing(true);
    try {
      const r = await api.post(`/admin/vendors/${id}/review`, {}, { timeout: 290000 });
      toast.success(`Review updated${r.data?.score != null ? ` · ${r.data.score}/100` : ""}`);
      await load(true);
    } catch {
      toast.error("Review could not run");
    } finally {
      setReviewing(false);
    }
  };

  if (loading) return <Loading />;

  if (!v) return (
    <Page>
      <Empty action={<TextLink onClick={() => navigate("/hiring")}>Back to hiring</TextLink>}>Applicant not found.</Empty>
    </Page>
  );

  const jobs = v.assigned_jobs || [];
  const docs = v.documents || [];
  const avail = v.availability || {};
  const tier = v.title === "Owner" ? "Owner" : TIER_LABEL[v.tier] || (v.tier != null ? `Tier ${v.tier}` : null);
  const eq = v.application_profile?.equipment_structured && v.application_profile?.equipment ? v.application_profile.equipment : null;
  const gta = v.application_profile?.location?.in_gta === true ? " (GTA)" : v.application_profile?.location?.in_gta === false ? " (outside the GTA)" : "";

  return (
    <Page testid="applicant-detail" width={880}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: -12 }}>
        <TextLink onClick={() => navigate("/hiring")}>Hiring</TextLink>
        {showOnboardingPack && <TextLink href={ONBOARDING_PACK_URL} testid="hiring-onboarding-pack">Onboarding pack</TextLink>}
      </div>

      <div>
        <h1 style={{ fontSize: 30, fontWeight: 500, letterSpacing: -1, margin: 0, lineHeight: 1.15 }}>{v.name}</h1>
        <Meta style={{ fontSize: 13.5, marginTop: 6, whiteSpace: "normal" }} parts={[v.title, v.email, tier].filter(Boolean)} />
        <div data-testid="applicant-source" style={{ fontSize: 13.5, color: p.ink3, marginTop: 4 }}>
          Found us via {sourceLabel(v.source, v.source_detail) || "unknown"}
        </div>
        <div style={{ display: "flex", gap: "8px 16px", alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
          <StatusText attention={!v.assignable}>{v.assignable ? "Assignable" : (v.assignable_reason || "Not assignable")}</StatusText>
          <TradeBadge v={v} />
          <VettingChip v={v} />
          <ReviewScorePill review={v.applicant_review} dark={dark} />
          <TierInsuranceFlag v={v} />
        </div>
      </div>

      {v.onboarding_status === "pending" && (
        <div data-testid="applicant-decision" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "14px 0", borderTop: `1px solid ${wantsDecision ? T.brick : p.hair}`, borderBottom: `1px solid ${wantsDecision ? T.brick : p.hair}` }}>
          <div style={{ fontSize: 13.5, color: T.brickDeep, fontWeight: 500 }}>Needs your decision<span style={{ color: p.ink3, fontWeight: 400 }}>. The automatic review never approves or rejects.</span></div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="ghost" disabled={busy} onClick={() => act("reject")} style={{ padding: "7px 14px", fontSize: 12 }}>Reject</Btn>
            <Btn disabled={busy} onClick={() => act("approve", 1)} style={{ padding: "7px 14px", fontSize: 12 }}>{busy ? "Saving" : "Approve, tier C"}</Btn>
          </div>
        </div>
      )}

      {v.onboarding_status === "rejected" && (
        <div data-testid="applicant-undo-reject" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "14px 0", borderTop: `1px solid ${p.hair}`, borderBottom: `1px solid ${p.hair}` }}>
          <div style={{ fontSize: 13.5, color: p.ink2 }}>Rejected{v.rejected_at ? ` on ${fmtDay(v.rejected_at)}` : ""}<span style={{ color: p.ink3 }}>. Undo moves them back to pending and sends no email.</span></div>
          <Btn variant="ghost" disabled={busy} onClick={() => act("unreject")} style={{ padding: "7px 14px", fontSize: 12 }}>{busy ? "Saving" : "Undo reject"}</Btn>
        </div>
      )}

      {v.onboarding_status === "approved" && v.onboarding && (
        <Section testid="applicant-onboarding" title="Onboarding" note={
          v.onboarding.state === "live" ? `Live${v.onboarding.completed_at ? ` since ${fmtDay(v.onboarding.completed_at)}` : ""}. Gets jobs automatically.`
            : v.onboarding.state === "paused" ? "Paused. Gets no jobs until you resume."
            : `${v.onboarding.done} of ${v.onboarding.total} done. Jobs start automatically when every step is done.`
        }>
          {v.onboarding.state !== "live" && (
            <List>
              {(v.onboarding.steps || []).map((s) => (
                <Row key={s.key} title={s.label} status={s.done ? (s.required ? "Done" : "Not needed") : "Not yet"} attention={!s.done} />
              ))}
            </List>
          )}
          <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
            <Btn variant="ghost" disabled={busy} data-testid="vendor-pause-toggle" onClick={() => act(v.is_active ? "pause" : "resume")} style={{ padding: "7px 14px", fontSize: 12 }}>
              {busy ? "Saving" : v.is_active ? "Pause" : "Resume"}
            </Btn>
          </div>
        </Section>
      )}

      <Section title="Resume">
        {v.resume?.url ? (
          <a data-testid="applicant-resume" href={v.resume.url} target="_blank" rel="noreferrer" style={{ fontSize: 13.5, color: p.ink }}>Download {v.resume.name || "resume"}</a>
        ) : (
          <div style={{ fontSize: 13.5, color: p.ink3 }}>No resume on file. They applied before resumes were required.</div>
        )}
      </Section>

      <Section title="Terms">
        <Facts p={p} rows={[
          ["Payout", v.payout_label || "Not set"],
          ["Rate", v.title === "Owner" ? "Full package, no deduction. Platform cut is never shown on owner earnings." : "Independent contractor on the fixed rate card."],
          ["Jobs", <span style={{ fontFamily: F.mono, fontSize: 12.5 }}>{jobs.length}</span>],
        ]} />
      </Section>

      <div>
        <ApplicantReviewSection review={v.applicant_review} p={p} dark={dark} />
        <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
          <button type="button" data-testid="review-rerun" disabled={reviewing} onClick={rerun} style={{ background: "none", border: "none", padding: 0, cursor: reviewing ? "default" : "pointer", fontFamily: F.display, fontSize: 13, color: reviewing ? p.ink4 : p.ink2, textDecoration: "underline", textUnderlineOffset: 3 }}>
            {reviewing ? "Reviewing, up to a minute" : v.applicant_review ? "Re-run review" : "Run review"}
          </button>
        </div>
      </div>

      <Section title="Skills">
        <div style={{ fontSize: 13.5, color: p.ink2 }}>{(v.skills || []).map(humanize).join(", ") || "None listed"}</div>
      </Section>

      <Section testid={availabilityLines(avail) ? "applicant-availability" : undefined} title="Service area and availability" note={availabilityLines(avail) ? "Self-reported when they applied." : null}>
        <Facts p={p} rows={[
          ["City", `${v.home_city || "Not given"}${gta}`],
          ["Areas", (v.service_areas || []).join(", ") || "Not given"],
          ...(availabilityLines(avail) ? availabilityLines(avail).map(([k, val]) => [k, String(val)]) : [
            ["Status", humanize(v.status) || "Unknown"],
            ["Hours", <span style={{ fontFamily: F.mono, fontSize: 12.5 }}>{avail.work_start || "08:00"} to {avail.work_end || "17:00"}</span>],
            ["Per day", `${avail.jobs_per_day ?? "?"} jobs. No availability form on file.`],
          ]),
        ]} />
      </Section>

      <Section title="Equipment" note="Self-reported.">
        {eq ? (
          <Facts p={p} rows={[["Camera", "camera_body"], ["Lenses", "lenses"], ["Tripod", "tripod"], ["Lighting", "lighting"], ["Drone", "drone_model"]].map(([label, key]) => [label, eq[key] || "None"])} />
        ) : (
          <div style={{ fontSize: 13.5, color: p.ink3 }}>{(v.equipment_list || []).join(", ") || "None listed"}</div>
        )}
      </Section>

      <Section title="Advanced RPAS">
        <Facts p={p} rows={[
          ["Level", humanize(v.rpas_certificate_level) || "Not given"],
          ["Number", v.rpas_certificate_number ? <span style={{ fontFamily: F.mono, fontSize: 12.5 }}>{v.rpas_certificate_number}</span> : "Not given"],
          ["Certificate", v.rpas_certificate_url ? <a href={v.rpas_certificate_url} target="_blank" rel="noreferrer" style={{ color: p.ink }}>Open photo</a> : "Not on file. They upload it from their profile in the photographer app."],
        ]} />
      </Section>

      <VettingSection v={v} />

      <InsuranceSection v={v} p={p} onSaved={() => load(true)} />

      <Section title="Assigned jobs">
        {jobs.length === 0 ? <Empty>No jobs assigned yet.</Empty> : (
          <List>
            {jobs.map((j) => (
              <Row key={j.id} title={j.address} meta={[j.package_name || "Package", j.scheduled_date ? { text: fmtDay(j.scheduled_date), figure: true } : "Not scheduled"]} status={humanize(j.status)} />
            ))}
          </List>
        )}
      </Section>

      <Section title="Documents">
        {docs.length === 0 ? <Empty>No documents yet.</Empty> : (
          <List>
            {docs.map((d) => (
              <Row key={d.id} title={d.doc_type === "coi" ? "Certificate of insurance" : humanize(d.doc_type)} meta={[{ text: fmtDay(d.created_at), figure: true }]} status={humanize(d.status)} attention={d.status === "pending"} />
            ))}
          </List>
        )}
      </Section>
    </Page>
  );
}
